import { RefObject } from 'react'

import { PointCloudOctree, Potree } from '@datalabs-jp/three-loader'
import { RootState } from '@editor/store/app'
import { useFrame } from '@react-three/fiber'
import { useSelector } from 'react-redux'
import { PerspectiveCamera, Scene } from 'three'

import { Steps, Tools } from '../definitions'

/**
 * Run our own render loop to render the two scenes side by side.
 *
 * @param {
 *    sceneLeftRef: RefObject<Scene>
 *    sceneRightRef: RefObject<Scene>
 * } props Scene references
 */
const Render = ({
  leftPotree,
  rightPotree,
  sceneLeftRef,
  sceneRightRef,
  fineTuneSceneRef,
  mainSceneRef,
  cameraLeft,
  cameraRight,
  fineTuneCamera,
  mainCamera,
}: {
  leftPotree: Potree
  rightPotree: Potree
  sceneLeftRef: RefObject<Scene | null>
  sceneRightRef: RefObject<Scene | null>
  fineTuneSceneRef: RefObject<Scene | null>
  mainSceneRef: RefObject<Scene | null>
  cameraLeft: PerspectiveCamera
  cameraRight: PerspectiveCamera
  fineTuneCamera: PerspectiveCamera
  mainCamera: PerspectiveCamera
}) => {
  const currentStep = useSelector((state: RootState) => state.pcdAlignment.currentStep)
  const currentTool = useSelector((state: RootState) => state.pcdAlignment.currentTool)
  const canvasSize = useSelector((state: RootState) => state.pcdAlignment.canvasRect)

  /**
   * Render loop. This will override the default render loop.
   */
  useFrame(({ gl }) => {
    if (currentTool === Tools.Overview) {
      if (!mainSceneRef.current) return
      const { width, height } = canvasSize

      const mainScenePotreePCDs = mainSceneRef.current?.children.filter((pcd) => pcd instanceof PointCloudOctree) || []
      rightPotree.updatePointClouds(mainScenePotreePCDs, mainCamera, gl)

      gl.setScissor(0, 0, width, height)
      gl.setViewport(0, 0, width, height)
      gl.render(mainSceneRef.current, mainCamera)
    } else if (currentTool === Tools.PCDAlignment && currentStep === Steps.FineTuneOrSave) {
      if (!fineTuneSceneRef.current) return
      const { width, height } = canvasSize

      const fineTunePotreePCDs =
        fineTuneSceneRef.current?.children.filter((pcd) => pcd instanceof PointCloudOctree) || []
      rightPotree.updatePointClouds(fineTunePotreePCDs, fineTuneCamera, gl)

      gl.setScissor(0, 0, width, height)
      gl.setViewport(0, 0, width, height)
      gl.render(fineTuneSceneRef.current, fineTuneCamera)
    } else {
      if (!sceneLeftRef.current || !sceneRightRef.current || !cameraLeft || !cameraRight) return
      const { width, height } = canvasSize

      const leftPotreePCDs = sceneLeftRef.current?.children.filter((pcd) => pcd instanceof PointCloudOctree) || []
      leftPotree.updatePointClouds(leftPotreePCDs, cameraLeft, gl)

      const rightPotreePCDs = sceneRightRef.current?.children.filter((pcd) => pcd instanceof PointCloudOctree) || []
      rightPotree.updatePointClouds(rightPotreePCDs, cameraRight, gl)

      gl.setScissor(0, 0, width / 2, height)
      gl.setViewport(0, 0, width / 2, height)
      gl.render(sceneLeftRef.current, cameraLeft)

      gl.setScissor(width / 2, 0, width / 2, height)
      gl.setViewport(width / 2, 0, width / 2, height)
      gl.render(sceneRightRef.current, cameraRight)
    }
  }, 1)

  return null
}

export default Render
